import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

/**
 * Navbar component - public top navigation
 * 
 * Automatically hides on admin pages (which use sidebar navigation).
 * Shows only public-facing pages for unauthenticated users.
 */
const Navbar = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);

  const toggleLanguage = (lang) => {
    i18n.changeLanguage(lang);
    setLangOpen(false);
  };

  const isActive = (path) => location.pathname === path;

  // Hide navbar on admin pages & authenticated pages (they use sidebar navigation)
  const adminRoutes = ['/dashboard', '/data-grid', '/calendar', '/university', '/address', '/property-manager', '/session-manager', '/user-bookings'];
  const isAdminRoute = adminRoutes.some(route => location.pathname.startsWith(route));
  const isCreateProperty = location.pathname.startsWith('/admin/');
  
  if (isAdminRoute || isCreateProperty) {
    return null;
  }

  return (
    <nav className="bg-white shadow-sm border-b border-gray-100 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16 items-center">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <div className="w-9 h-9 bg-primary-500 rounded-xl flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
            </div>
            <span className="font-bold text-xl text-gray-900">Nhà Trọ<span className="text-primary-500">KTX</span></span>
          </Link>

          {/* Desktop Navigation - Public pages only */}
          <div className="hidden md:flex items-center space-x-8">
            <Link
              to="/"
              className={`font-medium transition-colors duration-200 ${
                isActive('/') ? 'text-primary-600' : 'text-gray-600 hover:text-primary-600'
              }`}
            >
              {t('nav.home')}
            </Link>
            <Link
              to="/facets"
              className={`font-medium transition-colors duration-200 ${
                isActive('/facets') ? 'text-primary-600' : 'text-gray-600 hover:text-primary-600'
              }`}
            >
              {t('nav.facets')}
            </Link>
            <Link
              to="/saved"
              className={`font-medium transition-colors duration-200 flex items-center gap-1 ${
                isActive('/saved') ? 'text-primary-600' : 'text-gray-600 hover:text-primary-600'
              }`}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
              </svg>
              {t('nav.saved')}
            </Link>
            <Link
              to="/find-by-request"
              className={`font-medium transition-colors duration-200 ${
                isActive('/find-by-request') ? 'text-primary-600' : 'text-gray-600 hover:text-primary-600'
              }`}
            >
              {t('nav.findByRequest')}
            </Link>

            {/* Language Switcher */}
            <div className="relative">
              <button
                onClick={() => setLangOpen(!langOpen)}
                className="flex items-center space-x-1 px-3 py-2 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-all duration-200 text-sm font-medium text-gray-700"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{i18n.language === 'vi' ? 'VI' : 'EN'}</span>
                <svg className={`w-3 h-3 transition-transform duration-200 ${langOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {langOpen && (
                <div className="absolute right-0 mt-2 w-40 bg-white rounded-xl shadow-lg border border-gray-100 py-1 z-50">
                  <button
                    onClick={() => toggleLanguage('en')}
                    className={`w-full text-left px-4 py-2.5 text-sm hover:bg-primary-50 transition-colors flex items-center space-x-2 ${
                      i18n.language === 'en' ? 'text-primary-600 font-medium' : 'text-gray-700'
                    }`}
                  >
                    <span className="text-lg">🇬🇧</span>
                    <span>{t('common.english')}</span>
                  </button>
                  <button
                    onClick={() => toggleLanguage('vi')}
                    className={`w-full text-left px-4 py-2.5 text-sm hover:bg-primary-50 transition-colors flex items-center space-x-2 ${
                      i18n.language === 'vi' ? 'text-primary-600 font-medium' : 'text-gray-700'
                    }`}
                  >
                    <span className="text-lg">🇻🇳</span>
                    <span>{t('common.vietnamese')}</span>
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              {mobileMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile Menu - Public pages only */}
        {mobileMenuOpen && (
          <div className="md:hidden pb-4 border-t border-gray-100 pt-4">
            <div className="flex flex-col space-y-2">
              <Link
                to="/"
                onClick={() => setMobileMenuOpen(false)}
                className={`px-4 py-2.5 rounded-lg font-medium ${
                  isActive('/') ? 'bg-primary-50 text-primary-600' : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                {t('nav.home')}
              </Link>
              <Link
                to="/facets"
                onClick={() => setMobileMenuOpen(false)}
                className={`px-4 py-2.5 rounded-lg font-medium ${
                  isActive('/facets') ? 'bg-primary-50 text-primary-600' : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                {t('nav.facets')}
              </Link>
              <Link
                to="/saved"
                onClick={() => setMobileMenuOpen(false)}
                className={`px-4 py-2.5 rounded-lg font-medium ${
                  isActive('/saved') ? 'bg-primary-50 text-primary-600' : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                {t('nav.saved')}
              </Link>
              <Link
                to="/find-by-request"
                onClick={() => setMobileMenuOpen(false)}
                className={`px-4 py-2.5 rounded-lg font-medium ${
                  isActive('/find-by-request') ? 'bg-primary-50 text-primary-600' : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                {t('nav.findByRequest')}
              </Link>
              <div className="border-t border-gray-100 pt-2 mt-2">
                <p className="px-4 text-xs text-gray-400 uppercase tracking-wider font-medium mb-2">{t('nav.language')}</p>
                <button
                  onClick={() => { toggleLanguage('en'); setMobileMenuOpen(false); }}
                  className={`w-full text-left px-4 py-2.5 rounded-lg text-sm flex items-center space-x-2 ${
                    i18n.language === 'en' ? 'bg-primary-50 text-primary-600' : 'text-gray-600'
                  }`}
                >
                  <span>🇬🇧</span>
                  <span>{t('common.english')}</span>
                </button>
                <button
                  onClick={() => { toggleLanguage('vi'); setMobileMenuOpen(false); }}
                  className={`w-full text-left px-4 py-2.5 rounded-lg text-sm flex items-center space-x-2 ${
                    i18n.language === 'vi' ? 'bg-primary-50 text-primary-600' : 'text-gray-600'
                  }`}
                >
                  <span>🇻🇳</span>
                  <span>{t('common.vietnamese')}</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Overlay for language dropdown */}
      {langOpen && (
        <div className="fixed inset-0 z-40" onClick={() => setLangOpen(false)} />
      )}
    </nav>
  );
};

export default Navbar;